import React, { useCallback, useEffect, useState } from 'react';
import { IS_STRIPPED_DOWN } from '../lib/mode';

interface Props {
  apiBase:         string;
  marketId?:       string;
  marketTitle?:    string;
}

type Kind = 'oracle' | 'bet' | 'all';

interface Entry {
  id:            string;
  kind:          'oracle' | 'bet';
  user_id:       string;
  market_id:     string;
  outcome_id:    string;
  outcome_label: string;
  text:          string;
  vibe:          number | null;
  shares:        number | null;
  price:         number | null;
  cost:          number | null;
  multiplier:    number | null;
  created_at:    string;
}

// ─────────────────────────────────────────────────────────────────────────────

function prettyUser(id: string): string {
  if (!id.startsWith('bot_')) return id.charAt(0).toUpperCase() + id.slice(1);
  return id.slice(4)
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function userAccent(id: string): string {
  if (id.startsWith('bot_')) return '#a78bfa';
  return '#7dd3fc';
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (isNaN(then)) return iso;
  const diff = (Date.now() - then) / 1000;
  if (diff < 60)        return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function HistoryPanel({ apiBase, marketId, marketTitle }: Props): React.JSX.Element {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [filter,  setFilter]  = useState<Kind>('all');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const fetchEntries = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (marketId) qs.set('market_id', marketId);
    qs.set('limit', '120');
    fetch(`${apiBase}/api/multi/reasoning?${qs.toString()}`, { cache: 'no-store' })
      .then(r => r.json())
      .then(d => { setEntries(d.entries ?? []); setError(null); })
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [apiBase, marketId]);

  useEffect(() => {
    fetchEntries();
    // Socket.io removed — rely on the 15s poller alone.
    const t = setInterval(fetchEntries, 15_000);
    return () => { clearInterval(t); };
  }, [fetchEntries, apiBase]);

  // Session B recovery (2026-05-13, Phase C revision-2 / gate-22
  // Finding 2): on Arena (IS_STRIPPED_DOWN), oracle/vibe entries
  // are held back per DECISIONS.md 2026-05-13 I-046 — the GOSSIP
  // filter, the "VIBE X%" badge in row bodies, and the ceremonial
  // SENTINELS OF THE WARD header are all held-back surfaces. Pre-
  // filter entries here so oracle rows never render even if the
  // backend serves them; the trade history below stays as the
  // load-bearing audit log.
  const visibleEntries = IS_STRIPPED_DOWN
    ? entries.filter(e => e.kind !== 'oracle')
    : entries;
  const shown = filter === 'all' ? visibleEntries : visibleEntries.filter(e => e.kind === filter);

  return (
    <div style={{
      backgroundColor: '#0f172a',
      border:          '1px solid #1e293b',
      borderTop:       '2px solid #7c3aed',
      borderRadius:    8,
      overflow:        'hidden',
    }}>
      {/* ── Header ───────────────────────────────────────── */}
      <div style={{
        padding:        '12px 16px',
        borderBottom:   '1px solid #1e293b',
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'center',
        gap:            10,
        flexWrap:       'wrap',
      }}>
        <div>
          <p style={{ color: IS_STRIPPED_DOWN ? '#7dd3fc' : '#c084fc', fontSize: 10, letterSpacing: '0.22em',
            margin: 0, fontWeight: 700, fontFamily: 'monospace' }}>
            {IS_STRIPPED_DOWN ? 'ACTIVITY LOG' : 'SENTINELS OF THE WARD'}
          </p>
          <p style={{ color: '#475569', fontSize: 9, letterSpacing: '0.14em',
            margin: '3px 0 0', fontFamily: 'monospace' }}>
            {marketTitle ?? 'ALL MARKETS'} · {visibleEntries.length} entries
          </p>
        </div>

        <div style={{ display: 'flex', gap: 4, fontFamily: 'monospace' }}>
          {/* Session B Phase C revision-2 (2026-05-13, gate-22
              Finding 2): the GOSSIP filter selects oracle / vibe
              entries (held-back per I-046). Filtered out of the
              filter strip when stripped — only ALL + TRADES remain,
              and ALL means trades-only because oracle entries are
              pre-filtered above. */}
          {((
            IS_STRIPPED_DOWN
              ? [
                  { id: 'all', label: 'ALL',    color: '#94a3b8' },
                  { id: 'bet', label: 'TRADES', color: '#7dd3fc' },
                ]
              : [
                  { id: 'all',    label: 'ALL',     color: '#94a3b8' },
                  { id: 'oracle', label: 'GOSSIP',  color: '#a78bfa' },
                  { id: 'bet',    label: 'TRADES',  color: '#7dd3fc' },
                ]
          ) as Array<{ id: Kind; label: string; color: string }>).map(f => {
            const active = filter === f.id;
            return (
              <button key={f.id} onClick={() => setFilter(f.id)}
                style={{
                  background:   active ? `${f.color}14` : 'none',
                  border:       `1px solid ${active ? f.color : '#1e293b'}`,
                  color:        active ? f.color : '#475569',
                  fontSize:     9,
                  letterSpacing:'0.16em',
                  padding:      '3px 10px',
                  borderRadius: 3,
                  cursor:       'pointer',
                  fontFamily:   'monospace',
                  transition:   'color 0.15s, border-color 0.15s, background 0.15s',
                }}>
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────── */}
      <div style={{ maxHeight: 560, overflowY: 'auto' }}>
        {error && (
          <p style={{ color: 'rgba(239,68,68,0.8)', fontSize: 11,
            padding: '14px 16px', margin: 0, fontFamily: 'monospace' }}>
            {error}
          </p>
        )}

        {!error && shown.length === 0 && (
          <p style={{ color: '#475569', fontSize: 11, padding: '28px 16px',
            margin: 0, fontFamily: 'monospace', textAlign: 'center' }}>
            {loading ? 'Loading…' : 'No entries yet.'}
          </p>
        )}

        {shown.map(e => (
          <Row key={e.id} entry={e} />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Row({ entry: e }: { entry: Entry }) {
  const accent = userAccent(e.user_id);
  const isBot  = e.user_id.startsWith('bot_');
  const who    = prettyUser(e.user_id);

  return (
    <div style={{
      padding:      '14px 16px',
      borderBottom: '1px solid #111a2e',
      display:      'grid',
      gridTemplateColumns: '88px 1fr auto',
      gap:          14,
      alignItems:   'start',
    }}>
      {/* Left: kind + timestamp */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{
          display:       'inline-block',
          padding:       '2px 7px',
          borderRadius:  3,
          fontSize:      8,
          letterSpacing: '0.18em',
          fontFamily:    'monospace',
          width:         'max-content',
          border:        `1px solid ${e.kind === 'oracle' ? '#4c1d95' : '#1e3a5a'}`,
          color:         e.kind === 'oracle' ? '#c084fc' : '#7dd3fc',
          background:    e.kind === 'oracle' ? 'rgba(167,139,250,0.08)' : 'rgba(125,211,252,0.08)',
        }}>
          {e.kind === 'oracle' ? '◈ GOSSIP' : '▲ TRADE'}
        </span>
        <span style={{ color: '#475569', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
          {relativeTime(e.created_at)}
        </span>
      </div>

      {/* Middle: who + outcome + text */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: isBot ? "'Cormorant Garamond', serif" : 'monospace',
            fontSize:   isBot ? 15 : 12,
            fontStyle:  isBot ? 'italic' : 'normal',
            fontWeight: isBot ? 500 : 700,
            color:      accent,
          }}>
            {who}
          </span>
          <span style={{ color: '#334155', fontSize: 10 }}>·</span>
          <span style={{ color: '#94a3b8', fontSize: 11, fontFamily: 'monospace' }}>
            {e.outcome_label}
          </span>
          {e.kind === 'oracle' && e.vibe !== null && (
            <>
              <span style={{ color: '#334155', fontSize: 10 }}>·</span>
              <span style={{ color: '#f59e0b', fontSize: 10, fontFamily: 'monospace' }}>
                VIBE {(e.vibe * 100).toFixed(0)}%
              </span>
            </>
          )}
        </div>
        <p style={{
          color:      e.kind === 'oracle' ? '#cbd5e1' : '#94a3b8',
          fontSize:   12,
          lineHeight: 1.55,
          margin:     0,
          fontFamily: e.kind === 'oracle' ? "'Cormorant Garamond', serif" : 'monospace',
          fontStyle:  e.kind === 'oracle' ? 'italic' : 'normal',
        }}>
          {e.kind === 'oracle' ? `"${e.text}"` : e.text}
        </p>
      </div>

      {/* Right: metrics */}
      <div style={{ textAlign: 'right', fontFamily: 'monospace', minWidth: 80 }}>
        {e.kind === 'bet' && e.cost !== null && (
          <>
            <div style={{ color: '#86efac', fontSize: 11, fontWeight: 700 }}>
              ${e.cost.toFixed(2)}
            </div>
            {/* The "X× BONUS" column exposes the Prophecy Multiplier
                mechanic — held back per DECISIONS.md 2026-05-13 I-046.
                Per inventory §3, the HistoryPanel itself stays mounted
                but this specific column is gated when stripped. */}
            {!IS_STRIPPED_DOWN && e.multiplier !== null && e.multiplier >= 2 && (
              <div style={{ color: '#fcd34d', fontSize: 9, marginTop: 2, letterSpacing: '0.1em' }}>
                {e.multiplier.toFixed(1)}× BONUS
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
